import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import ComboSelect from "@/components/ComboSelect";
import { StatusChip, ConfidenceBadge } from "@/components/Bits";
import { toast } from "sonner";
import { MagicWand, Archive, CheckCircle, X } from "@phosphor-icons/react";

// Small pill showing where the suggestion came from
function SourcePill({ source }) {
    if (!source) return null;
    if (source === "learned") {
        return (
            <span className="text-[10px] font-semibold rounded-sm px-1.5 py-0.5"
                  style={{ background: "var(--se-posted-bg)", color: "var(--se-posted-text)" }}
                  data-testid="pill-learned">
                learned
            </span>
        );
    }
    return (
        <span className="text-[10px] font-semibold rounded-sm px-1.5 py-0.5"
              style={{ background: "var(--se-plum-soft)", color: "var(--se-plum)" }}
              data-testid="pill-ai">
            AI
        </span>
    );
}

export default function ReconcileModal({ open, onClose, lineIds, ledgers, onDone, statementLabel }) {
    // lineIds is an array of bank_line ids we need to reconcile.
    // We fetch/refresh their state from the server as edits happen.
    const [lines, setLines] = useState([]);
    const [overrides, setOverrides] = useState({});     // {line_id: ledger_id or "loan:xxx"}
    const [loans, setLoans] = useState([]);
    const [pendingAdvances, setPendingAdvances] = useState([]);
    const [pendingClaims, setPendingClaims] = useState([]);
    const [pendingPetty, setPendingPetty] = useState([]);
    const [busy, setBusy] = useState(false);
    const [suggesting, setSuggesting] = useState(false);
    const [bulkBusy, setBulkBusy] = useState(false);

    const suspense = useMemo(
        () => (ledgers || []).find((l) => l.name === "Suspense Account"),
        [ledgers]
    );

    // Combined options — normal ledgers plus loan-flavoured pseudo-ledgers.
    // Two loan variants exist:
    //   loan_pay:<id>  → OUT (debit line) → post as loan payment (Dr interest / principal)
    //   loan_in:<id>   → IN  (credit line) → post as loan disbursement (Cr Loan Payable)
    // We surface only the variant relevant to the direction of THIS bank line
    // so the user can't accidentally book a repayment as income and vice-versa.
    const ledgerOpts = useMemo(
        () => (ledgers || []).map((x) => ({ id: x.id, name: x.name, hint: x.group_name })),
        [ledgers]
    );
    const activeLoans = useMemo(() => (loans || []).filter((l) => l.status === "active"), [loans]);

    const optionsFor = (line) => {
        const isCredit = Number(line?.credit || 0) > 0;
        const isDebit = Number(line?.debit || 0) > 0;
        const loanOpts = [];
        if (isDebit) {
            for (const l of activeLoans) {
                loanOpts.push({
                    id: `loan_pay:${l.id}`,
                    name: `Loan payment — ${l.name}`,
                    hint: l.loan_type === "settlement" ? "settlement · reduces liability"
                        : l.loan_type === "od" ? "OD · 100% interest"
                        : "term · auto-split next EMI",
                });
            }
        }
        if (isCredit) {
            for (const l of activeLoans) {
                // Settlement loans are one-shot — additional disbursements aren't meaningful.
                if (l.loan_type === "settlement") continue;
                loanOpts.push({
                    id: `loan_in:${l.id}`,
                    name: `Loan received — ${l.name}`,
                    hint: "disbursement · Cr Loan Payable (not income)",
                });
            }
        }
        // Employee-advance disbursements — only relevant for debit lines
        // where the bank matches the intent and the amount is within a
        // reasonable tolerance (±5%).
        const advOpts = [];
        const claimOpts = [];
        if (isDebit) {
            const bankLedger = (ledgers || []).find((x) => x.name === `${line.bank} Bank Account`);
            const bankId = bankLedger?.id;
            const amount = Number(line.debit || 0);
            for (const a of pendingAdvances) {
                if (bankId && a.intended_source_ledger_id !== bankId) continue;
                const exp = Number(a.expected_amount || a.amount || 0);
                if (exp <= 0 || Math.abs(exp - amount) / Math.max(exp, amount) > 0.05) continue;
                advOpts.push({
                    id: `adv:${a.id}`,
                    name: `Employee advance — ${a.employee_name} · ₹${exp.toFixed(2)}`,
                    hint: `${a.reason?.slice(0, 40) || ""}${a.ref_no ? ` · ${a.ref_no}` : ""}`,
                });
            }
            // Expense-claim reimbursements — same pattern as advances.
            for (const c of pendingClaims) {
                if (bankId && c.intended_source_ledger_id !== bankId) continue;
                const exp = Number(c.expected_amount || c.total_amount || 0);
                if (exp <= 0 || Math.abs(exp - amount) / Math.max(exp, amount) > 0.05) continue;
                claimOpts.push({
                    id: `claim:${c.id}`,
                    name: `Expense claim ${c.claim_no} — ${c.claimant_name} · ₹${exp.toFixed(2)}`,
                    hint: `${c.reason?.slice(0, 40) || ""}${c.ref_no ? ` · ${c.ref_no}` : ""}`,
                });
            }
            // Petty-cash disbursements — same pattern; posts Dr Petty Cash Advance / Cr Bank.
            for (const p of pendingPetty) {
                if (bankId && p.intended_source_ledger_id !== bankId) continue;
                const exp = Number(p.expected_amount || p.amount || 0);
                if (exp <= 0 || Math.abs(exp - amount) / Math.max(exp, amount) > 0.05) continue;
                claimOpts.push({
                    id: `petty:${p.id}`,
                    name: `Petty cash — ${p.employee_name} · ₹${exp.toFixed(2)}`,
                    hint: `${p.reason?.slice(0, 40) || ""}${p.ref_no ? ` · ${p.ref_no}` : ""}`,
                });
            }
        }
        return [...advOpts, ...claimOpts, ...loanOpts, ...ledgerOpts];
    };

    // Translate a picker choice into a /bank-lines/{id}/confirm request body.
    const bodyForChoice = (choice) => {
        const s = String(choice);
        if (s.startsWith("adv:")) return { advance_disbursement_id: s.slice("adv:".length) };
        if (s.startsWith("claim:")) return { expense_claim_disbursement_id: s.slice("claim:".length) };
        if (s.startsWith("petty:")) return { petty_cash_disbursement_id: s.slice("petty:".length) };
        if (s.startsWith("loan_pay:")) return { loan_id: s.slice("loan_pay:".length) };
        if (s.startsWith("loan_in:")) return { disbursement_loan_id: s.slice("loan_in:".length) };
        // legacy prefix kept for backwards compat (older learned suggestions)
        if (s.startsWith("loan:")) return { loan_id: s.slice("loan:".length) };
        return { ledger_id: choice };
    };

    const refresh = async () => {
        if (!lineIds?.length) { setLines([]); return; }
        const { data } = await api.get("/bank-lines", { params: { limit: 2000, offset: 0 }});
        const items = data.items || [];
        const idSet = new Set(lineIds);
        const rows = items.filter((r) => idSet.has(r.id));
        rows.sort((a, b) => lineIds.indexOf(a.id) - lineIds.indexOf(b.id));
        setLines(rows);
    };

    // On open: fetch lines + loans + pending advances/claims. No auto-suggest —
    // the accountant explicitly clicks "Auto-suggest all" or a per-line Suggest
    // button when they want AI input.
    useEffect(() => {
        if (!open) return;
        (async () => {
            await refresh();
            try {
                const { data } = await api.get("/loans", { params: { status: "active" }});
                setLoans(data || []);
            } catch (_e) { /* non-critical */ }
            try {
                const { data } = await api.get("/employee-advances/pending-bank");
                setPendingAdvances(data || []);
            } catch (_e) { /* non-critical */ }
            try {
                const { data } = await api.get("/expense-claims/pending-bank");
                setPendingClaims(data || []);
            } catch (_e) { /* non-critical */ }
            try {
                const { data } = await api.get("/petty-cash/requests/pending-bank");
                setPendingPetty(data || []);
            } catch (_e) { /* non-critical */ }
        })();
    }, [open, JSON.stringify(lineIds || [])]);

    const pending = lines.filter((l) => l.status !== "posted" && l.status !== "inter_bank");
    const posted = lines.length - pending.length;

    const setLedgerFor = (lineId, ledgerId) => {
        setOverrides((o) => ({ ...o, [lineId]: ledgerId }));
    };
    const chosenLedger = (l) => overrides[l.id] || l.suggested_ledger_id || "";

    const postOne = async (l, forcedLedgerId) => {
        const choice = forcedLedgerId || chosenLedger(l);
        if (!choice) { toast.error("Choose a ledger or use Suspense"); return; }
        setBusy(true);
        try {
            const body = bodyForChoice(choice);
            const { data } = await api.post(`/bank-lines/${l.id}/confirm`, body);
            const isAdvance = String(choice).startsWith("adv:");
            const isClaim = String(choice).startsWith("claim:");
            const isPetty = String(choice).startsWith("petty:");
            if (data.loan_split) {
                const s = data.loan_split;
                toast.success(`Loan payment posted (int ₹${s.interest.toFixed(2)} · prin ₹${s.principal.toFixed(2)} · settle ₹${s.settlement.toFixed(2)})`);
            } else if (data.new_outstanding != null) {
                toast.success(`Loan disbursement received · new outstanding ₹${Number(data.new_outstanding).toFixed(2)}`);
            } else if (isAdvance) {
                toast.success("Employee advance disbursement posted · EMI recovery unlocked");
                // Refresh pending advances so it disappears from other lines' suggestions.
                try {
                    const { data: pa } = await api.get("/employee-advances/pending-bank");
                    setPendingAdvances(pa || []);
                } catch (_e) { /* non-critical */ }
            } else if (isClaim) {
                toast.success("Expense-claim reimbursement posted · claim marked disbursed");
                try {
                    const { data: pc } = await api.get("/expense-claims/pending-bank");
                    setPendingClaims(pc || []);
                } catch (_e) { /* non-critical */ }
            } else if (isPetty) {
                toast.success("Petty cash disbursement posted · advance ledger updated");
                try {
                    const { data: pp } = await api.get("/petty-cash/requests/pending-bank");
                    setPendingPetty(pp || []);
                } catch (_e) { /* non-critical */ }
            } else {
                toast.success("Posted");
            }
            await refresh();
            onDone?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const sendToSuspense = async (l) => {
        if (!suspense) { toast.error("Suspense ledger not seeded — please contact admin"); return; }
        return postOne(l, suspense.id);
    };

    const suggestOne = async (l) => {
        setBusy(true);
        try {
            await api.post(`/bank-lines/${l.id}/suggest`);
            await refresh();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const suggestAll = async () => {
        setSuggesting(true);
        try {
            for (const l of pending) {
                try { await api.post(`/bank-lines/${l.id}/suggest`); } catch (_e) { /* ignore */ }
            }
            await refresh();
            toast.success("Suggestions refreshed");
        } finally { setSuggesting(false); }
    };

    const postAllSuggested = async () => {
        setBulkBusy(true);
        let ok = 0, skip = 0;
        for (const l of pending) {
            const choice = chosenLedger(l);
            if (!choice) { skip += 1; continue; }
            const body = bodyForChoice(choice);
            try { await api.post(`/bank-lines/${l.id}/confirm`, body); ok += 1; }
            catch (_e) { skip += 1; }
        }
        await refresh();
        setBulkBusy(false);
        toast.success(`Posted ${ok} · Skipped ${skip}`);
    };

    const sendRemainingToSuspense = async () => {
        if (!suspense) { toast.error("Suspense ledger not seeded"); return; }
        setBulkBusy(true);
        let ok = 0;
        let current = pending;
        while (current.length) {
            const l = current[0];
            try { await api.post(`/bank-lines/${l.id}/confirm`, { ledger_id: suspense.id }); ok += 1; }
            catch (_e) { break; }
            await refresh();
            const resp = (await api.get("/bank-lines", { params: { limit: 2000, offset: 0 }})).data;
            current = (resp.items || [])
                .filter((r) => lineIds.includes(r.id) && r.status !== "posted" && r.status !== "inter_bank");
        }
        setBulkBusy(false);
        toast.success(`Sent ${ok} to Suspense`);
    };

    const done = pending.length === 0;
    const hasPending = pending.length > 0;

    const handleClose = () => {
        // Plain close — leave lines intact so the user can come back later.
        onClose?.();
        onDone?.();
    };

    const handleDismiss = async () => {
        // Explicit "discard unmapped & close" — only invoked by the footer button.
        if (hasPending) {
            const toDiscard = pending.map((l) => l.id);
            try {
                const { data } = await api.post("/bank-lines/discard", { line_ids: toDiscard });
                if (data.discarded > 0) toast.info(`Discarded ${data.discarded} unmapped line${data.discarded === 1 ? "" : "s"} — re-upload the statement any time`);
            } catch (_e) { /* ignore */ }
        }
        onClose?.();
        onDone?.();
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
            <DialogContent className="max-w-6xl max-h-[92vh] overflow-hidden flex flex-col p-0" data-testid="reconcile-modal">
                <DialogHeader className="px-6 pt-6 pb-3 border-b" style={{ borderColor: "var(--se-border)" }}>
                    <DialogTitle className="font-serif text-xl">Reconcile bank lines {statementLabel ? <span className="text-sm text-stone-500 font-sans">— {statementLabel}</span> : null}</DialogTitle>
                    <DialogDescription className="text-xs">
                        {lines.length} line{lines.length === 1 ? "" : "s"} to review · {posted} posted · {pending.length} pending.
                        <br />
                        AI suggestions <b>do not touch the ledger</b> until you hit <b>Post</b>. Safe to close (X) any time — pending lines stay put and you can come back via <b>Reconcile pending</b> on the Bank statements page.
                    </DialogDescription>
                </DialogHeader>

                <div className="px-6 py-3 flex items-center gap-2 border-b bg-stone-50" style={{ borderColor: "var(--se-border)" }}>
                    <Button variant="outline" size="sm" onClick={suggestAll} disabled={suggesting || bulkBusy} data-testid="modal-suggest-all">
                        <MagicWand size={14} className="mr-1.5" /> {suggesting ? "Suggesting…" : "Auto-suggest all"}
                    </Button>
                    <Button size="sm" onClick={postAllSuggested} disabled={bulkBusy || suggesting || pending.length === 0}
                            style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="modal-post-suggested">
                        <CheckCircle size={14} className="mr-1.5" /> Post all with suggestions
                    </Button>
                    <Button variant="outline" size="sm" onClick={sendRemainingToSuspense} disabled={bulkBusy || pending.length === 0}
                            data-testid="modal-suspense-all">
                        <Archive size={14} className="mr-1.5" /> Send remaining to Suspense
                    </Button>
                    <div className="ml-auto text-xs text-stone-500">
                        {done ? "✓ All lines resolved — you can close this modal." : "Post each line, send to Suspense if unclear, or dismiss to handle later."}
                    </div>
                </div>

                <div className="overflow-auto flex-1">
                    <table className="se-table text-sm" data-testid="reconcile-table">
                        <thead>
                            <tr>
                                <th style={{ width: 60 }}>Bank</th>
                                <th style={{ width: 92 }}>Date</th>
                                <th>Narration</th>
                                <th style={{ width: 120 }} className="text-right">Amount</th>
                                <th style={{ width: 260 }}>Map to ledger</th>
                                <th style={{ width: 230 }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lines.length === 0 ? (
                                <tr><td colSpan="6" className="text-center py-10 text-stone-500">No lines to reconcile.</td></tr>
                            ) : lines.map((l) => {
                                const isPosted = l.status === "posted";
                                const isInter = l.status === "inter_bank";
                                const led = chosenLedger(l);
                                const suggestedName = l.suggested_ledger_name;
                                return (
                                    <tr key={l.id} data-testid={`recon-row-${l.id}`} style={{ background: isPosted ? "var(--se-posted-bg)" : (isInter ? "var(--se-indigo-soft)" : "transparent") }}>
                                        <td className="text-xs">{l.bank}</td>
                                        <td className="font-mono text-xs">{l.date || "—"}</td>
                                        <td>
                                            <div className="text-sm max-w-[380px]" title={l.narration}>{l.narration}</div>
                                            {l.ref ? <div className="text-[10px] text-stone-500 font-mono">{l.ref}</div> : null}
                                            <div className="flex items-center gap-1 mt-1">
                                                <StatusChip status={l.status} />
                                                {l.suggested_source ? <SourcePill source={l.suggested_source} /> : null}
                                                {l.confidence != null ? <ConfidenceBadge value={l.confidence} /> : null}
                                            </div>
                                            {l.suggested_reason ? <div className="text-[11px] text-stone-500 italic mt-0.5">{l.suggested_reason}</div> : null}
                                        </td>
                                        <td className="se-num">
                                            {l.credit ? <span style={{ color: "#1e4620" }}>+ ₹{inr(l.credit)}</span> : null}
                                            {l.debit ? <span style={{ color: "#991b1b" }}>- ₹{inr(l.debit)}</span> : null}
                                        </td>
                                        <td>
                                            {isInter ? (
                                                <span className="text-xs text-stone-500 italic">Netted with sibling · no post needed</span>
                                            ) : isPosted ? (
                                                <span className="text-xs" style={{ color: "var(--se-posted-text)" }}>Posted to {ledgers.find((x) => x.id === l.mapped_ledger_id)?.name || "—"}</span>
                                            ) : (
                                                <div className="space-y-1">
                                                    <ComboSelect
                                                        value={led}
                                                        onValueChange={(v) => setLedgerFor(l.id, v)}
                                                        options={optionsFor(l)}
                                                        pinnedIds={suspense ? [suspense.id] : []}
                                                        placeholder={suggestedName ? `Suggested: ${suggestedName}` : "Choose ledger or loan"}
                                                        testId={`recon-ledger-${l.id}`}
                                                        size="sm"
                                                    />
                                                    <RefundHint line={l} ledgerId={led} ledgers={ledgers} />
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {isInter || isPosted ? null : (
                                                <div className="flex gap-1 flex-wrap">
                                                    <Button size="sm" variant="ghost" onClick={() => suggestOne(l)} disabled={busy}
                                                            data-testid={`recon-suggest-${l.id}`}
                                                            title="Ask AI for a ledger suggestion for this line only">
                                                        <MagicWand size={12} className="mr-1" /> Suggest
                                                    </Button>
                                                    <Button size="sm" onClick={() => postOne(l)} disabled={busy || !led}
                                                            style={{ background: "var(--se-brand)", color: "#fff" }}
                                                            data-testid={`recon-post-${l.id}`}>
                                                        Post
                                                    </Button>
                                                    <Button size="sm" variant="outline" onClick={() => sendToSuspense(l)} disabled={busy}
                                                            data-testid={`recon-suspense-${l.id}`}>
                                                        <Archive size={12} className="mr-1" /> Suspense
                                                    </Button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="px-6 py-3 border-t flex items-center justify-between bg-stone-50" style={{ borderColor: "var(--se-border)" }}>
                    <div className="text-xs text-stone-500">
                        {done
                            ? "All resolved."
                            : `${pending.length} pending — close (X) to keep them for later, or discard to remove and re-upload.`}
                    </div>
                    <div className="flex gap-2">
                        {hasPending ? (
                            <Button variant="ghost" size="sm" onClick={handleDismiss} data-testid="recon-discard"
                                    className="text-rose-700 hover:text-rose-800 hover:bg-rose-50">
                                <X size={14} className="mr-1" /> Discard unmapped & close
                            </Button>
                        ) : null}
                        <Button variant="outline" size="sm" onClick={handleClose} data-testid="recon-close">
                            {done ? "Close" : "Keep for later & close"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}


function RefundHint({ line, ledgerId, ledgers }) {
    if (!ledgerId) return null;
    const l = ledgers.find((x) => x.id === ledgerId);
    if (!l) return null;
    const debit = Number(line.debit || 0);
    const credit = Number(line.credit || 0);
    // Customer + outgoing money (debit on our bank statement) = refund to customer.
    if (l.party_type === "customer" && debit > 0) {
        return (
            <div className="text-[10px] font-medium" style={{ color: "#0d6efd" }} data-testid={`refund-hint-${line.id}`}>
                ↩ Looks like a refund to this customer
            </div>
        );
    }
    // Vendor + incoming money (credit on our bank statement) = refund from vendor.
    if (l.party_type === "vendor" && credit > 0) {
        return (
            <div className="text-[10px] font-medium" style={{ color: "#0d6efd" }} data-testid={`refund-hint-${line.id}`}>
                ↩ Looks like a refund from this vendor
            </div>
        );
    }
    return null;
}
